import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@/components/ui/accordion';
import { PublicLayout } from '@/components/public/PublicLayout';
import { Reveal } from '@/components/public/anim';
import { Seo } from '@/components/Seo';
import { Search, HelpCircle, Mail, Leaf } from 'lucide-react';

export default function Faq() {
  const { t } = useTranslation();
  const [cats, setCats] = useState(null);
  const [faqs, setFaqs] = useState(null);
  const [q, setQ] = useState('');

  useEffect(() => {
    api.get('/cms/faq-categories').then(({ data }) => setCats(data || [])).catch(() => setCats([]));
    api.get('/cms/faqs').then(({ data }) => setFaqs(data || [])).catch(() => setFaqs([]));
  }, []);

  const filtered = useMemo(() => {
    if (!faqs) return [];
    const term = q.trim().toLowerCase();
    if (!term) return faqs;
    return faqs.filter((f) => (f.question || '').toLowerCase().includes(term) || (f.answer || '').toLowerCase().includes(term));
  }, [faqs, q]);

  const grouped = useMemo(() => {
    const byCat = new Map();
    (cats || []).forEach((c) => byCat.set(c.id, { name: c.name, items: [] }));
    byCat.set('_none', { name: 'Generale', items: [] });
    filtered.forEach((f) => {
      const key = byCat.has(f.category_id) ? f.category_id : '_none';
      byCat.get(key).items.push(f);
    });
    return Array.from(byCat.values()).filter((g) => g.items.length > 0);
  }, [cats, filtered]);

  const loading = cats === null || faqs === null;

  const faqJsonLd = (faqs && faqs.length > 0) ? {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.slice(0, 20).map((f) => ({
      '@type': 'Question', name: f.question,
      acceptedAnswer: { '@type': 'Answer', text: f.answer },
    })),
  } : null;

  return (
    <PublicLayout>
      <Seo title={t('pages.faq.title')} description={t('pages.faq.subtitle')} path="/faq" jsonLd={faqJsonLd} />
      <section className="sila-section pb-8 pt-28">
        <Reveal>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-[hsl(var(--brand-sun)/0.22)] px-3 py-1 text-xs font-semibold uppercase tracking-wide text-[hsl(var(--brand-bark))]"><Leaf className="h-3.5 w-3.5" />{t('pages.faq.eyebrow')}</span>
          <h1 className="mt-4 font-display text-4xl font-bold tracking-tight sm:text-5xl">{t('pages.faq.title')}</h1>
          <p className="mt-3 max-w-2xl text-lg text-muted-foreground">{t('pages.faq.subtitle')}</p>
          <div className="relative mt-8 max-w-lg">
            <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input className="h-12 rounded-full pl-10" placeholder={t('pages.faq.searchPh')} value={q} onChange={(e) => setQ(e.target.value)} data-testid="faq-search" />
          </div>
        </Reveal>
      </section>

      <section className="sila-section max-w-3xl pb-16">
        {loading && <div className="space-y-3">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-16 w-full rounded-2xl" />)}</div>}

        {!loading && grouped.length === 0 && (
          <div className="rounded-[24px] border border-dashed border-border py-20 text-center" data-testid="faq-empty">
            <HelpCircle className="mx-auto h-10 w-10 text-muted-foreground/50" />
            <p className="mt-3 text-muted-foreground">{q ? t('pages.faq.noResults') : t('pages.faq.empty')}</p>
          </div>
        )}

        {!loading && grouped.map((g, gi) => (
          <div key={gi} className="mb-8" data-testid={`faq-group-${gi}`}>
            <h2 className="mb-3 font-display text-xl font-semibold">{g.name}</h2>
            <Accordion type="single" collapsible className="space-y-3">
              {g.items.map((f) => (
                <AccordionItem key={f.id} value={f.id} className="rounded-2xl border border-border bg-card px-4" data-testid={`faq-item-${f.id}`}>
                  <AccordionTrigger className="text-left text-base font-medium hover:no-underline">{f.question}</AccordionTrigger>
                  <AccordionContent className="text-muted-foreground">{f.answer}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        ))}
      </section>

      <section className="sila-section pb-24">
        <Reveal>
          <div className="flex flex-col items-center gap-4 rounded-[28px] bg-secondary px-6 py-12 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/12 text-primary"><Mail className="h-6 w-6" /></span>
            <h2 className="font-display text-2xl font-bold">{t('pages.faq.ctaTitle')}</h2>
            <p className="max-w-md text-muted-foreground">{t('pages.faq.ctaText')}</p>
            <Button asChild className="rounded-full" data-testid="faq-cta"><Link to="/#newsletter">{t('pages.faq.ctaBtn')}</Link></Button>
          </div>
        </Reveal>
      </section>
    </PublicLayout>
  );
}
